/**
 * AcceptCTA Component
 * Call-to-action with e-sign modal and signature capture
 */

import React, { useState, useRef } from 'react';
import { esignAPI } from '../../../utils/api';

const styles = {
  container: {
    padding: '60px 20px',
    marginBottom: '40px',
  },
  ctaBox: {
    maxWidth: '600px',
    margin: '0 auto',
    padding: '40px',
    backgroundColor: '#1A2332',
    borderRadius: '8px',
    border: '2px solid #0057FF',
    textAlign: 'center',
  },
  title: {
    fontSize: '28px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '12px',
  },
  description: {
    fontSize: '16px',
    color: '#BBBEC7',
    marginBottom: '32px',
    lineHeight: '1.6',
  },
  button: {
    padding: '16px 32px',
    borderRadius: '6px',
    border: 'none',
    backgroundColor: '#00D4AA',
    color: '#0F1729',
    fontSize: '16px',
    fontWeight: '700',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  buttonHover: {
    backgroundColor: '#00B899',
    boxShadow: '0 12px 24px rgba(0, 212, 170, 0.3)',
    transform: 'translateY(-2px)',
  },
  buttonLoading: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
  // Modal styles
  modal: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000,
    animation: 'fadeIn 0.3s ease-in-out',
  },
  modalContent: {
    backgroundColor: '#1A2332',
    borderRadius: '8px',
    padding: '40px',
    maxWidth: '500px',
    width: '90%',
    maxHeight: '90vh',
    overflowY: 'auto',
    border: '1px solid #2A3444',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: '700',
    color: '#FFFFFF',
    marginBottom: '24px',
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
  },
  label: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#FFFFFF',
    marginBottom: '8px',
  },
  input: {
    padding: '12px 16px',
    borderRadius: '6px',
    border: '1px solid #2A3444',
    backgroundColor: '#0F1729',
    color: '#FFFFFF',
    fontSize: '14px',
    fontFamily: 'inherit',
  },
  inputFocus: {
    borderColor: '#0057FF',
    outline: 'none',
    boxShadow: '0 0 0 2px rgba(0, 87, 255, 0.1)',
  },
  checkbox: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '12px',
    marginBottom: '20px',
  },
  checkboxInput: {
    marginTop: '3px',
    cursor: 'pointer',
  },
  checkboxLabel: {
    fontSize: '13px',
    color: '#BBBEC7',
    lineHeight: '1.5',
  },
  actions: {
    display: 'flex',
    gap: '12px',
    marginTop: '24px',
  },
  actionButton: {
    flex: 1,
    padding: '12px 16px',
    borderRadius: '6px',
    border: '1px solid #2A3444',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  submitButton: {
    backgroundColor: '#0057FF',
    color: '#FFFFFF',
    border: 'none',
  },
  submitButtonHover: {
    backgroundColor: '#003D99',
  },
  cancelButton: {
    backgroundColor: 'transparent',
    color: '#BBBEC7',
  },
  cancelButtonHover: {
    backgroundColor: '#2A3444',
  },
  error: {
    color: '#FF6B6B',
    fontSize: '13px',
    marginTop: '4px',
  },
};

/**
 * ESignModal Component
 */
function ESignModal({ isOpen, onClose, prospect, onSuccess }) {
  const [formData, setFormData] = useState({
    fullName: '',
    title: '',
    date: new Date().toISOString().split('T')[0],
    agreedToTerms: false,
  });
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    // Clear error for this field
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    if (!formData.title.trim()) {
      newErrors.title = 'Title is required';
    }
    if (!formData.date) {
      newErrors.date = 'Date is required';
    }
    if (!formData.agreedToTerms) {
      newErrors.agreedToTerms = 'You must agree to the terms';
    }

    return newErrors;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError(null);

    const newErrors = validateForm();
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsSubmitting(true);

    try {
      await esignAPI.submitSignature(prospect.id, {
        fullName: formData.fullName,
        title: formData.title,
        date: formData.date,
        agreedToTerms: formData.agreedToTerms,
        signedAt: new Date().toISOString(),
      });

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Failed to submit signature:', err);
      setSubmitError(err.message || 'Failed to submit. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) {
    return null;
  }

  return (
    <div style={styles.modal} onClick={onClose}>
      <div
        style={styles.modalContent}
        onClick={(e) => e.stopPropagation()}
      >
        <h2 style={styles.modalTitle}>Accept Proposal</h2>

        <form style={styles.form} onSubmit={handleSubmit}>
          {submitError && (
            <div style={styles.error}>{submitError}</div>
          )}

          {/* Full Name */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="fullName">
              Full Name *
            </label>
            <input
              id="fullName"
              style={styles.input}
              type="text"
              name="fullName"
              value={formData.fullName}
              onChange={handleInputChange}
              placeholder="Enter your full name"
              disabled={isSubmitting}
            />
            {errors.fullName && (
              <div style={styles.error}>{errors.fullName}</div>
            )}
          </div>

          {/* Title */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="title">
              Job Title *
            </label>
            <input
              id="title"
              style={styles.input}
              type="text"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              placeholder="e.g., Chief Operating Officer"
              disabled={isSubmitting}
            />
            {errors.title && (
              <div style={styles.error}>{errors.title}</div>
            )}
          </div>

          {/* Date */}
          <div style={styles.formGroup}>
            <label style={styles.label} htmlFor="date">
              Date *
            </label>
            <input
              id="date"
              style={styles.input}
              type="date"
              name="date"
              value={formData.date}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            {errors.date && (
              <div style={styles.error}>{errors.date}</div>
            )}
          </div>

          {/* Terms checkbox */}
          <div style={styles.checkbox}>
            <input
              id="agreedToTerms"
              style={styles.checkboxInput}
              type="checkbox"
              name="agreedToTerms"
              checked={formData.agreedToTerms}
              onChange={handleInputChange}
              disabled={isSubmitting}
            />
            <label style={styles.checkboxLabel} htmlFor="agreedToTerms">
              I have reviewed the proposal and agree to the terms and conditions.
              Talk Australia may contact me regarding the implementation.
            </label>
          </div>
          {errors.agreedToTerms && (
            <div style={styles.error}>{errors.agreedToTerms}</div>
          )}

          {/* Actions */}
          <div style={styles.actions}>
            <button
              style={{
                ...styles.actionButton,
                ...styles.cancelButton,
              }}
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#2A3444';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';
              }}
            >
              Cancel
            </button>
            <button
              style={{
                ...styles.actionButton,
                ...styles.submitButton,
              }}
              type="submit"
              disabled={isSubmitting}
              onMouseEnter={(e) => {
                if (!isSubmitting) {
                  e.target.style.backgroundColor = '#003D99';
                }
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#0057FF';
              }}
            >
              {isSubmitting ? 'Submitting...' : 'Accept & Sign'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

/**
 * AcceptCTA Component
 */
function AcceptCTA({ prospect, onAcceptanceCompleted }) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <div style={styles.container}>
      <div style={styles.ctaBox}>
        <h2 style={styles.title}>Ready to Get Started?</h2>
        <p style={styles.description}>
          Click the button below to review and accept your custom proposal.
          Our team will follow up within 24 hours to begin the implementation.
        </p>

        <button
          style={styles.button}
          onClick={() => setIsModalOpen(true)}
          onMouseEnter={(e) => {
            e.target.style.backgroundColor = '#00B899';
            e.target.style.boxShadow = '0 12px 24px rgba(0, 212, 170, 0.3)';
            e.target.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.target.style.backgroundColor = '#00D4AA';
            e.target.style.boxShadow = 'none';
            e.target.style.transform = 'translateY(0)';
          }}
        >
          Accept Proposal
        </button>
      </div>

      {/* E-sign Modal */}
      <ESignModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        prospect={prospect}
        onSuccess={onAcceptanceCompleted}
      />
    </div>
  );
}

AcceptCTA.propTypes = {
  prospect: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'object') {
      return new Error(`${propName} must be an object`);
    }
  },
  onAcceptanceCompleted: function (props, propName) {
    if (props[propName] != null && typeof props[propName] !== 'function') {
      return new Error(`${propName} must be a function`);
    }
  },
};

export default AcceptCTA;
